import React, { Fragment, useMemo } from "react";
import { Link } from "wouter";

import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../../components/src/react-table/features";
import { sortBy } from "lodash";

import { twoDecPriceFormat } from "../../../../../common/src/utils/FormatFunctions";
import { ICommandBarMenuItem } from "../../../../../components/src/common/CommandBar";
import { ReactDataGrid } from "../../../../../components/src/ReactDataGrid";
import { getRandomFileName } from "../../../../../components/src/common/exportToXlsx2";
import { OpPaymentStatusEnum } from "../../../../../common/src/types.generated";
import { TooltipLabel } from "../../../../../components/src/TooltipLabel";
import { OPPaymentStatusEnumDescriptions } from "../../../types.generated";

export interface IPaymentDataRow {
    _id: string;
    clientId: string;
    creditorAgent: string;
    clientName: string;
    instructedAmount: number;
    createTimestamp: string;
    updateTimestamp: string;
    requestedExecutionDate: string;
    transactionStatus: OpPaymentStatusEnum;
    remittanceInformationUnstructured: string; // reference
    updatedByUserName: string;
    debtorAccountIban: string;
    creditorAccountIban: string;
    signingBasketId: string;
}

interface IPaymentsGrid {
    items: IPaymentDataRow[];
    commands?: ICommandBarMenuItem[];
    visibleColumns?: string[];
    onSelectedChange?(items: any[]): any;
    showExcelButton?: boolean;
}

// control visibility and the order of columns
const defaultVisibleColumns = [
    "_id",
    "clientName",
    "instructedAmount",
    "requestedExecutionDate",
    "createTimestamp",
    "updateTimestamp",
    "transactionStatus",
    "remittanceInformationUnstructured",
    "updatedByUserName",
    "debtorAccountIban",
    "creditorAccountIban"
];

const groupPaymentsByStatus = (payments: IPaymentDataRow[], statusGroups: string[]) => {
    const result = {};
    statusGroups.forEach((status) => {
        result[status] = [];
    });

    const readyToSign = [OpPaymentStatusEnum.PDNG, OpPaymentStatusEnum.RCVD];

    const authorizedPendingExecution = [
        OpPaymentStatusEnum.ACCP,
        OpPaymentStatusEnum.ACSP,
        OpPaymentStatusEnum.ACWC,
        OpPaymentStatusEnum.ACWP
    ];

    const executed = [OpPaymentStatusEnum.ACSC, OpPaymentStatusEnum.ACCC, OpPaymentStatusEnum.ACFC, OpPaymentStatusEnum.ACTC];

    payments.forEach((payment) => {
        if (readyToSign.includes(payment.transactionStatus)) {
            result["Ready to sign"].push(payment);
        } else if (authorizedPendingExecution.includes(payment.transactionStatus)) {
            result["Authorized, pending execution"].push(payment);
        } else if (payment.transactionStatus === OpPaymentStatusEnum.PATC || payment.transactionStatus === OpPaymentStatusEnum.PART) {
            result["Need counter signature, sign in bank for now"].push(payment);
        } else if (executed.includes(payment.transactionStatus)) {
            result["Executed"].push(payment);
        } else {
            result["Error/Cancelled"].push(payment);
        }
    });

    return result;
};

export const PaymentsGrid = ({
    items,
    commands,
    visibleColumns,
    onSelectedChange,
    showExcelButton = true
}: IPaymentsGrid): React.ReactElement => {
    const statusGroups = useMemo(() => {
        return [
            "Ready to sign",
            "Authorized, pending execution",
            "Need counter signature, sign in bank for now",
            "Executed",
            "Error/Cancelled"
        ];
    }, []);

    //const [location] = useLocation();
    //const [queryArgs] = useSearchParams();

    const onSelectionChange = (items: any[]) => {
        if (onSelectedChange) {
            onSelectedChange(items);
        }
    };

    const allColumns = useMemo<ReactTableColumnDef<IPaymentDataRow>[]>(() => {
        /* const relativeLocation = (id: string): string => {
            const pathname = location.split("?")[0];
            const parts: string[] = pathname.split("/");
            const last = parts[parts.length - 1];
            if (isValidMongoDBObjectID(last)) {
                const path = pathname.replace(last, "");
                if (queryArgs.toString()) {
                    return `${path}${id}?${queryArgs.toString()}`;
                } else {
                    return `${path}${id}}`;
                }
            } else {
                const path = pathname.endsWith("/") ? pathname : `${pathname}/`;
                if (queryArgs.toString()) {
                    return `${path}${id}?${queryArgs.toString()}`;
                } else {
                    return `${path}${id}}`;
                }
            }
        };*/
        const columnHelper = createReactTableColumnHelper<IPaymentDataRow>();
        const allColumns = [
            columnHelper.accessor("_id", {
                header: "Id",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <Link key={value} to={`/openpayments/payments/${value}`}>
                            {"#"}
                        </Link>
                    );
                },
                size: 10
            }),
            columnHelper.accessor("clientName", {
                header: "Client",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <Link key={value} to={`/parties/${cellProps.row.original.clientId}`}>
                            {value}
                        </Link>
                    );
                },
                size: 90
            }),
            columnHelper.accessor("instructedAmount", {
                header: "Amount",
                cell: (cellProps) => {
                    const value = cellProps.getValue().toString();
                    return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(parseFloat(value))}</div>;
                },
                size: 25
            }),
            columnHelper.accessor("requestedExecutionDate", {
                header: "Requested execution date",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                },
                size: 75
            }),
            columnHelper.accessor("createTimestamp", {
                header: "Create timestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                },
                size: 80
            }),
            columnHelper.accessor("transactionStatus", {
                header: "Status",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{<TooltipLabel text={value} content={OPPaymentStatusEnumDescriptions[value]} />}</div>;
                },
                size: 30
            }),
            columnHelper.accessor("remittanceInformationUnstructured", {
                header: "Reference",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            }),
            columnHelper.accessor("debtorAccountIban", {
                header: "Debtor account IBAN",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            }),
            columnHelper.accessor("creditorAccountIban", {
                header: "Creditor account IBAN",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            }),
            columnHelper.accessor("updateTimestamp", {
                header: "Update timestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                },
                size: 80
            }),
            columnHelper.accessor("updatedByUserName", {
                header: "Updated by",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            })
        ];
        return allColumns;
    }, []);

    const visibleColumnKeys: string[] = visibleColumns ? visibleColumns : defaultVisibleColumns;
    // we want to keep the order of visible columns as well
    const columns = visibleColumnKeys.map((c) => {
        return allColumns.find((x: any) => x.accessorKey == c);
    });

    return items && items.length > 0 ? (
        <Fragment>
            {statusGroups.map((status) => (
                <div key={status} className="mt-4 mb-4">
                    <h2>{status}</h2>
                    <ReactDataGrid
                        columns={columns}
                        data={sortBy(groupPaymentsByStatus(items, statusGroups)[status], "createTimestamp").reverse()}
                        commands={commands}
                        defaultHiddenColumns={null}
                        exportToXlsxFile={showExcelButton ? getRandomFileName() : null}
                        onSelectedChange={onSelectionChange}
                    />
                </div>
            ))}
        </Fragment>
    ) : (
        <Fragment>No data to display.</Fragment>
    );
};
